import { supabase } from "./supabase";
import { envValue } from "./env";
import type { Session, User } from "@supabase/supabase-js";

export async function signIn(email: string, password: string) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data;
}

export async function signOut() {
  await supabase.auth.signOut();
}

export async function getSession(): Promise<Session | null> {
  const { data } = await supabase.auth.getSession();
  return data.session;
}

export async function getUser(): Promise<User | null> {
  const { data } = await supabase.auth.getUser();
  return data.user;
}

export function onAuthChange(cb: (session: Session | null) => void) {
  return supabase.auth.onAuthStateChange((_event, session) => {
    cb(session);
  });
}

const SUPABASE_URL = envValue(import.meta.env.VITE_SUPABASE_URL);

/** Public booking endpoints — always use the anon key, never an admin session. */
export async function invokePublicEdgeFunction(name: string, body: unknown) {
  const anonKey = envValue(import.meta.env.VITE_SUPABASE_ANON_KEY);
  if (!SUPABASE_URL || !anonKey) {
    throw new Error("Booking is temporarily unavailable. Please contact us by email.");
  }

  const res = await fetch(`${SUPABASE_URL}/functions/v1/${name}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${anonKey}`,
      apikey: anonKey,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res
      .json()
      .catch(() => ({ error: "Request failed" }));
    throw new Error(err.error || err.message || "Request failed");
  }
  return res.json();
}

export async function invokeEdgeFunction(name: string, body: unknown) {
  const anonKey = envValue(import.meta.env.VITE_SUPABASE_ANON_KEY);

  // Public endpoints (booking / interest) work with the anon key. We only
  // attach the user's session token when it exists AND is still valid — a
  // stale/expired admin session must never be sent, otherwise the Supabase
  // gateway rejects the request with 401 ("Request failed") before the
  // function even runs.
  let token = anonKey;
  try {
    const session = await getSession();
    if (session?.access_token) {
      const expiresAtMs = session.expires_at ? session.expires_at * 1000 : 0;
      const stillValid = expiresAtMs > Date.now() + 60_000;
      if (stillValid) {
        token = session.access_token;
      } else {
        // Expired: try to refresh; fall back to anon key if that fails.
        const { data } = await supabase.auth.refreshSession();
        if (data.session?.access_token) {
          token = data.session.access_token;
        }
      }
    }
  } catch {
    // Any auth error → fall back to the anon key so public calls still work.
  }

  const res = await fetch(`${SUPABASE_URL}/functions/v1/${name}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`,
      "apikey": anonKey,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res
      .json()
      .catch(() => ({ error: "Request failed" }));
    throw new Error(err.error || err.message || "Request failed");
  }
  return res.json();
}
